import React, { Component } from 'react';
import { PropTypes as PT } from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { ReactComponent as CancelIcon } from '@lifesize/ux-assets/vectors/svgIcons/cancel.svg';
import { ReactComponent as ChatIcon } from '@lifesize/ux-assets/vectors/svgIcons/chat.svg';
import { ReactComponent as ComposeIcon } from '@lifesize/ux-assets/vectors/svgIcons/compose.svg';
import Button from 'components/Button/Button';
import ChatMessageInput from 'containers/Chat/ChatMessageInput/ChatMessageInputContainer';
import ChatConversationHeader from 'containers/Chat/ChatConversationHeader/ChatConversationHeaderContainer';
import Spinner from 'components/MainSpinner/MainSpinner';
import classes from './ChatWindow.module.scss';
import ChatConversation from './ChatConversation';
import ChatParticipants from './ChatParticipants';
import ChatConversationLoading from './ChatConversationLoading';

function renderEmptyParticipantList() {
  return (
    <div>
      <FormattedMessage
        id="components.chat.participants.empty"
        description="Inform user that the participant list is empty"
        defaultMessage="There are no{br}participants{br}in this chat."
        values={{
          br: <br />
        }}
      />
    </div>
  );
}

class ChatWindow extends Component {
  constructor(props) {
    super(props);

    this.state = {
      showParticipants: false
    };
  }

  showParticipants() {
    this.setState({ showParticipants: true });
  }

  render() {
    const {
      conversation,
      hasNewChat,
      isChatLoading,
      isFirstUse,
      isGettingMessages,
      mode,
      onBookmarkScroll,
      onGetMoreMessages,
      onStartNewChat,
      participants,
      resetChatFlag,
      scrollBookmarks
    } = this.props;
    const isMeeting = conversation && conversation.contact && conversation.contact.isMeeting;
    const participantsClasses = [classes.participants];

    if (this.state.showParticipants && isMeeting) {
      participantsClasses.push(classes.showParticipants);
    }

    if (isFirstUse && mode !== 'search' && !conversation) {
      return (
        <div className={`${classes.chatWindowContainer} ${classes.isFirstUse}`}>
          <div className={classes.chatWindowFirstUse}>
            <div className={classes.chatIcon}>
              <ChatIcon data-testid="chat-window-chat-icon" />
            </div>
            <p className={classes.headline}>
              <FormattedMessage
                id="components.chat.conversationEmptyHeader"
                description="Has the user had any chats?"
                defaultMessage="No chats yet?"
              />
            </p>
            <p className={classes.message}>
              <FormattedMessage
                id="components.chat.conversationEmptyMessage"
                description="Instructions for the user to start a new chat"
                defaultMessage="Click the {icon} button{br}to start a new chat or click below."
                values={{
                  br: <br />,
                  icon: <ComposeIcon data-testid="chat-window-compose-icon" className={classes.composeIcon} />
                }}
              />
            </p>
            <Button kind="secondary" onClick={onStartNewChat} dataAttrs={{ 'data-testid': 'start-new-chat-btn' }}>
              <FormattedMessage
                id="components.chat.conversationEmptyButton"
                description="Prompt the user to start a new chat"
                defaultMessage="Start a new chat"
              />
            </Button>
          </div>
        </div>
      );
    }

    if (isChatLoading) {
      return (
        <div>
          <Spinner
            config={{
              lines: 13,
              length: 17,
              width: 8,
              radius: 20,
              color: '#413675',
              speed: 0.7
            }}
          />
        </div>
      );
    }

    return (
      <div className={classes.chatWindowContainer}>
        {mode === 'search' && <div className={classes.fadeInChat} />}
        <ChatConversationHeader onShowParticipants={() => this.showParticipants()} />
        <div className={classes.conversationContainer}>
          <div className={classes.conversation}>
            <ChatConversation
              conversation={conversation}
              isGettingMessages={isGettingMessages}
              onBookmarkScroll={onBookmarkScroll}
              onGetMoreMessages={onGetMoreMessages}
              scrollBookmarks={scrollBookmarks}
              hasNewChat={hasNewChat}
              resetChatFlag={resetChatFlag}
            />
            <div className={classes.conversationCompose}>
              <ChatMessageInput conversation={conversation} />
            </div>
            {conversation && conversation.isLoading ? <ChatConversationLoading show={conversation.isLoading} /> : null}
          </div>
          <div className={participantsClasses.join(' ')} id="chatParticipants">
            <div className={classes.participantsHeader}>
              <div className={classes.headerTitle}>
                <FormattedMessage
                  id="components.chat.participants.header"
                  description="Chat Participants List"
                  defaultMessage="Chat Participants"
                />
              </div>
              <button
                className={classes.hideList}
                id="hideChatParticipants"
                onClick={() => {
                  this.setState({ showParticipants: false });
                }}
                tabIndex="-1"
              >
                <CancelIcon data-testid="cancelIcon" />
              </button>
            </div>
            <div className={classes.participantsList}>
              {isMeeting ? (
                <ChatParticipants
                  canRemove={false}
                  canSelect={false}
                  contacts={participants}
                  emptyMessage={renderEmptyParticipantList()}
                />
              ) : null}
            </div>
          </div>
        </div>
      </div>
    );
  }
}

ChatWindow.propTypes = {
  conversation: PT.object,
  hasNewChat: PT.bool,
  isChatLoading: PT.bool,
  isFirstUse: PT.bool,
  isGettingMessages: PT.bool,
  mode: PT.string,
  onBookmarkScroll: PT.func,
  onGetMoreMessages: PT.func,
  onStartNewChat: PT.func,
  participants: PT.array,
  resetChatFlag: PT.func,
  scrollBookmarks: PT.object
};

ChatWindow.defaultProps = {
  mode: 'current',
  isFirstUse: false
};

export default ChatWindow;
